import { Controller } from "@hotwired/stimulus";
import { setCriteriaField, setElementValue, syncHouseholdIncomes } from "./utils/criteria_fields";
import { applyFiltersForm } from "./utils/filters_form";
import { applyTaxProgramToggle, emitTaxProgramChange } from "./utils/tax_program";

// profiles (Stimulus controller) — panneau « Mes profils » du simulateur (#290)
//
// Ouvre un panneau qui glisse depuis la gauche listant les profils de
// l'utilisateur, et permet de :
// - sélectionner un profil (« Utiliser ce profil ») → chargement des critères
//   dans le formulaire `#edit-profile-form` sans rechargement de page,
// - dupliquer un profil (« Dupliquer ») → création d'une copie côté serveur
//   puis chargement de ses critères.
//
// Dans les deux cas, le backend mémorise le profil actif en session et renvoie
// ses critères en JSON, appliqués via `applyCriterias`. Un unique recalcul
// `api-sync` (scope `listing_details`) est déclenché après application.
export default class extends Controller {
    static targets = ["panel", "overlay", "list", "search", "status", "count"];

    static values = {
        listUrl: String,
        selectUrlTemplate: String,
        duplicateUrlTemplate: String,
        deleteUrlTemplate: String,
    };

    connect() {
        this.profiles = [];
        this.onKeydown = (event) => {
            if (event.key !== "Escape") return;
            // Échap ferme d'abord un éventuel menu kebab ouvert (#366), puis le
            // panneau si aucun menu n'est ouvert.
            if (this.closeMenus()) return;
            this.closePanel();
        };
        // Fermeture des menus kebab au clic extérieur (#366).
        this.onDocumentClick = (event) => {
            if (event.target.closest(".simulator-profiles-card-menu")) return;
            this.closeMenus();
        };
        document.addEventListener("click", this.onDocumentClick);
    }

    disconnect() {
        document.removeEventListener("keydown", this.onKeydown);
        document.removeEventListener("click", this.onDocumentClick);
    }

    // ---------- Ouverture / fermeture ----------
    openPanel(event) {
        if (event) event.preventDefault();
        if (this.hasPanelTarget) this.panelTarget.classList.add("is-open");
        if (this.hasOverlayTarget) this.overlayTarget.classList.add("is-open");
        if (this.hasSearchTarget) this.searchTarget.value = "";
        document.addEventListener("keydown", this.onKeydown);
        this.loadProfiles();
    }

    closePanel(event) {
        if (event) event.preventDefault();
        if (this.hasPanelTarget) this.panelTarget.classList.remove("is-open");
        if (this.hasOverlayTarget) this.overlayTarget.classList.remove("is-open");
        document.removeEventListener("keydown", this.onKeydown);
    }

    // ---------- Chargement de la liste ----------
    loadProfiles() {
        if (!this.hasListUrlValue || !this.listUrlValue) return;

        this.setStatus("Chargement…");
        if (this.hasListTarget) this.listTarget.innerHTML = "";

        fetch(this.listUrlValue, {
            headers: { Accept: "application/json" },
            credentials: "same-origin",
        })
            .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
            .then((json) => {
                this.profiles = Array.isArray(json.profiles) ? json.profiles : [];
                this.updateCount(this.profiles.length);
                this.renderProfiles(this.profiles);
            })
            .catch(() => this.setStatus("Impossible de charger vos profils."));
    }

    renderProfiles(profiles) {
        if (!this.hasListTarget) return;
        this.listTarget.innerHTML = "";

        if (profiles.length === 0) {
            this.setStatus("Aucun profil enregistré pour le moment.");
            return;
        }

        this.clearStatus();
        profiles.forEach((profile) => this.listTarget.appendChild(this.buildCard(profile)));
        this.applyFilter("");
    }

    // Carte du panneau « Mes profils » (#366) : la carte entière est cliquable
    // (= charge le profil, ex-bouton « Utiliser ce profil »), accessible au
    // clavier (`role=button` + Entrée/Espace). Les anciens boutons en clair sont
    // remplacés par un menu kebab (3 points verticaux) en haut à droite, ouvrant
    // un dropdown « Dupliquer » / « Supprimer » (ce dernier masqué pour le
    // profil principal).
    buildCard(profile) {
        const card = document.createElement("div");
        card.className = "simulator-profiles-card";
        if (profile.active) {
            card.classList.add("is-active");
        }
        card.dataset.profileLabel = String(profile.label || "").toLowerCase();
        card.dataset.profileId = String(profile.id);
        card.setAttribute("role", "button");
        card.setAttribute("tabindex", "0");
        card.dataset.action = "click->profiles#select keydown->profiles#selectOnKey";

        const header = document.createElement("div");
        header.className = "simulator-profiles-card-header";

        const name = document.createElement("span");
        name.className = "simulator-profiles-card-name";
        name.textContent = profile.label || "Profil sans nom";
        header.appendChild(name);

        const trailing = document.createElement("div");
        trailing.className = "simulator-profiles-card-trailing";

        if (profile.principal) {
            const badge = document.createElement("span");
            badge.className = "simulator-profiles-card-badge";
            badge.textContent = "Principal";
            trailing.appendChild(badge);
        }

        trailing.appendChild(this.buildMenu(profile));
        header.appendChild(trailing);
        card.appendChild(header);

        card.appendChild(this.buildModified(profile.updated_at_label));
        return card;
    }

    buildModified(label) {
        const modified = document.createElement("p");
        modified.className = "simulator-profiles-card-modified";
        modified.textContent = label || "";
        return modified;
    }

    // Menu kebab (3 points verticaux) + dropdown d'actions. Le profil principal
    // n'expose pas « Supprimer » (garde-fou, cf. backend `422`).
    buildMenu(profile) {
        const menu = document.createElement("div");
        menu.className = "simulator-profiles-card-menu";

        const kebab = document.createElement("button");
        kebab.type = "button";
        kebab.className = "simulator-profiles-card-kebab";
        kebab.dataset.action = "click->profiles#toggleMenu";
        kebab.setAttribute("aria-haspopup", "true");
        kebab.setAttribute("aria-expanded", "false");
        kebab.setAttribute("aria-label", "Actions du profil");
        kebab.innerHTML = '<i class="fa fa-ellipsis-v"></i>';
        menu.appendChild(kebab);

        const dropdown = document.createElement("div");
        dropdown.className = "simulator-profiles-card-dropdown";
        dropdown.setAttribute("role", "menu");
        dropdown.hidden = true;

        dropdown.appendChild(
            this.buildMenuItem("Dupliquer", profile.id, "click->profiles#duplicate")
        );
        if (!profile.principal) {
            dropdown.appendChild(
                this.buildMenuItem(
                    "Supprimer",
                    profile.id,
                    "click->profiles#destroy",
                    "simulator-profiles-card-dropdown-item--danger"
                )
            );
        }
        menu.appendChild(dropdown);
        return menu;
    }

    buildMenuItem(label, profileId, action, extraClass) {
        const item = document.createElement("button");
        item.type = "button";
        item.className = "simulator-profiles-card-dropdown-item";
        if (extraClass) item.classList.add(extraClass);
        item.textContent = label;
        item.dataset.profileId = String(profileId);
        item.dataset.action = action;
        item.setAttribute("role", "menuitem");
        return item;
    }

    // ---------- Recherche client ----------
    filter(event) {
        const query = event && event.target ? event.target.value : "";
        this.applyFilter(query);
    }

    applyFilter(query) {
        if (!this.hasListTarget) return;
        const needle = String(query || "").trim().toLowerCase();
        let visibleCount = 0;

        this.listTarget.querySelectorAll(".simulator-profiles-card").forEach((card) => {
            const match = !needle || (card.dataset.profileLabel || "").includes(needle);
            card.classList.toggle("d-none", !match);
            if (match) visibleCount += 1;
        });

        if (this.profiles.length > 0 && visibleCount === 0) {
            this.setStatus("Aucun profil ne correspond à votre recherche.");
        } else {
            this.clearStatus();
        }
    }

    // ---------- Menu kebab (#366) ----------
    // Ouvre/ferme le dropdown d'actions. `stopPropagation` empêche le clic sur le
    // kebab de déclencher la sélection du profil (carte cliquable).
    toggleMenu(event) {
        event.preventDefault();
        event.stopPropagation();

        const kebab = event.currentTarget;
        const menu = kebab.closest(".simulator-profiles-card-menu");
        if (!menu) return;

        const dropdown = menu.querySelector(".simulator-profiles-card-dropdown");
        if (!dropdown) return;

        const willOpen = dropdown.hidden;
        // Un seul dropdown ouvert à la fois.
        this.closeMenus();
        if (willOpen) {
            dropdown.hidden = false;
            kebab.setAttribute("aria-expanded", "true");
        }
    }

    // Ferme tous les dropdowns ouverts. Renvoie true si au moins un était ouvert.
    closeMenus() {
        if (!this.hasListTarget) return false;
        let closed = false;

        this.listTarget
            .querySelectorAll(".simulator-profiles-card-dropdown")
            .forEach((dropdown) => {
                if (!dropdown.hidden) {
                    dropdown.hidden = true;
                    closed = true;
                }
            });
        this.listTarget
            .querySelectorAll(".simulator-profiles-card-kebab")
            .forEach((kebab) => kebab.setAttribute("aria-expanded", "false"));

        return closed;
    }

    // ---------- Sélection / duplication / suppression ----------
    select(event) {
        const card = event.currentTarget;
        const id = card && card.dataset ? card.dataset.profileId : null;
        if (!id || !this.hasSelectUrlTemplateValue) return;

        this.postProfileAction(this.buildUrl(this.selectUrlTemplateValue, id), card);
    }

    // Sélection au clavier (carte `role=button`) : Entrée ou Espace.
    selectOnKey(event) {
        if (event.key !== "Enter" && event.key !== " " && event.key !== "Spacebar") return;
        event.preventDefault();
        this.select(event);
    }

    duplicate(event) {
        event.stopPropagation();
        this.closeMenus();

        const button = event.currentTarget;
        const id = button && button.dataset ? button.dataset.profileId : null;
        if (!id || !this.hasDuplicateUrlTemplateValue) return;

        this.postProfileAction(this.buildUrl(this.duplicateUrlTemplateValue, id), button);
    }

    // Suppression (soft-delete, #366) : confirmation puis POST. La carte est
    // retirée du panneau et le compteur décrémenté ; le profil reste en base
    // (`deleted_at` renseigné).
    destroy(event) {
        event.stopPropagation();
        this.closeMenus();

        const button = event.currentTarget;
        const id = button && button.dataset ? button.dataset.profileId : null;
        if (!id || !this.hasDeleteUrlTemplateValue) return;

        if (!window.confirm("Supprimer ce profil ? Cette action est irréversible.")) return;

        this.setButtonsDisabled(true);
        fetch(this.buildUrl(this.deleteUrlTemplateValue, id), {
            method: "POST",
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
                "X-CSRF-Token": this.csrfToken(),
            },
            credentials: "same-origin",
        })
            .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
            .then(() => this.removeProfileCard(id))
            .catch(() => this.setStatus("Impossible de supprimer ce profil, veuillez réessayer."))
            .finally(() => this.setButtonsDisabled(false));
    }

    removeProfileCard(id) {
        this.profiles = this.profiles.filter((profile) => String(profile.id) !== String(id));
        this.updateCount(this.profiles.length);

        if (this.hasListTarget) {
            const card = this.listTarget.querySelector(
                `.simulator-profiles-card[data-profile-id="${this.cssEscape(id)}"]`
            );
            if (card) card.remove();
        }

        if (this.profiles.length === 0) {
            this.setStatus("Aucun profil enregistré pour le moment.");
        }
    }

    postProfileAction(url, button) {
        if (!url) return;
        this.setButtonsDisabled(true);

        fetch(url, {
            method: "POST",
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
                "X-CSRF-Token": this.csrfToken(),
            },
            credentials: "same-origin",
        })
            .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
            .then((json) => {
                this.applyResponse(json);
                this.closePanel();
            })
            .catch(() => this.setStatus("Une erreur est survenue, veuillez réessayer."))
            .finally(() => this.setButtonsDisabled(false));
    }

    // ---------- Application des critères au formulaire ----------
    applyResponse(data) {
        const form = document.getElementById("edit-profile-form");
        if (!form || !data) return;

        const criterias = data.criterias || {};
        Object.keys(criterias).forEach((key) => this.setCriteriaField(form, key, criterias[key]));
        // « Salaires annuels nets du foyer » = 12 × les revenus mensuels du
        // profil chargé, comme au chargement de page (`household-incomes#initialSync`).
        // Sans ça un profil stocké avec un annuel désynchronisé (profils créés
        // avant #583) réaffiche sa valeur stale à la bascule de profil.
        syncHouseholdIncomes(form);
        this.applyTaxProgramToggle(form, criterias);
        this.updateProfileNameSection(Boolean(data.principal));
        // Resynchronise la baguette magique (#398) : son `href` est rendu côté
        // serveur avec le `profile_id` du chargement initial. Sans rechargement,
        // changer de profil doit réécrire ce lien sur le profil nouvellement
        // actif, faute de quoi le wizard éditerait le mauvais profil.
        this.updateWizardWandProfile(data.id);
        // Filtres catalogue (#305) : localisation + nombre de pièces, qui vivent
        // dans `#listing-filter-form` (et non `#edit-profile-form`).
        this.applyFiltersForm(criterias);
        // Rebascule sur l'onglet « Catalogue » (#305), sauf si pas d'accès catalogue.
        this.switchToCatalog();
        // Resynchronise les favoris sur le profil nouvellement actif (#305).
        this.applyFavorites(data);
        this.refreshSimulation();
        this.refreshListings();
    }

    // Délègue au helper mutualisé (`utils/criteria_fields`) afin que le wizard
    // (#307) et le panneau « Mes profils » partagent exactement la même logique
    // de binding vers les champs canoniques.
    setCriteriaField(form, key, value) {
        setCriteriaField(form, key, value);
    }

    setElementValue(el, value) {
        setElementValue(el, value);
    }

    // Aligne le sélecteur de mode (résidence / investissement) et déclenche
    // l'affichage/masquage des blocs liés à la fiscalité via le contrôleur
    // `toggle` (qui désactive aussi les champs des blocs masqués pour ne pas
    // polluer le payload api-sync).
    // Délègue à la logique mutualisée (`utils/tax_program`) partagée avec les
    // simulations (#529). `this.element` (colonne profil) est un descendant du
    // contrôleur `toggle` (`.simulator-panel`) : l'évènement `toggle:set` bubble
    // donc jusqu'à lui. Réveille aussi le bouton de fiscalité et le comparateur
    // via un `change` sur le radio `criterias[tax_program]` (#347).
    applyTaxProgramToggle(form, criterias) {
        applyTaxProgramToggle(this.element, form, criterias);
    }

    emitTaxProgramChange(form, taxProgram) {
        emitTaxProgramChange(form, taxProgram);
    }

    // Affiche le bloc « Nom du profil client » pour un profil secondaire et le
    // masque pour le profil principal. L'indicateur « enregistré
    // automatiquement » n'est plus géré ici : il est désormais affiché en
    // permanence, hors de ce bloc (#305).
    updateProfileNameSection(principal) {
        const section = document.querySelector("[data-profile-name]");
        if (!section) return;

        section.setAttribute("data-profile-principal", principal ? "true" : "false");
        section.classList.toggle("d-none", Boolean(principal));
    }

    // Réécrit le `href` de la baguette magique (#398) pour qu'il pointe sur le
    // profil nouvellement actif (`profileId`). Le lien est rendu côté serveur
    // avec un `profile_id` figé au chargement de la page ; sans rechargement, il
    // resterait sur le profil initial et le wizard éditerait le mauvais profil.
    // On conserve les autres paramètres (`profile_wizard=mode`, `source_id`).
    updateWizardWandProfile(profileId) {
        if (profileId == null) return;
        document.querySelectorAll(".simulator-wizard-wand").forEach((wand) => {
            const href = wand.getAttribute("href");
            if (!href) return;
            try {
                const url = new URL(href, window.location.origin);
                url.searchParams.set("profile_id", String(profileId));
                wand.setAttribute("href", url.pathname + url.search + url.hash);
            } catch (_) {
                wand.setAttribute(
                    "href",
                    href.replace(/([?&]profile_id=)[^&#]*/, `$1${encodeURIComponent(profileId)}`)
                );
            }
        });
    }

    // Applique les critères de filtres catalogue (#305 / #341) au formulaire
    // `#listing-filter-form`. Délègue au helper mutualisé (`utils/filters_form`)
    // afin que le wizard « Nouveau profil » (#307/#424) et le panneau « Mes
    // profils » partagent exactement la même logique de resynchronisation des
    // filtres (localisation, nombre de pièces, prix, orientation, étage, tri).
    applyFiltersForm(criterias) {
        applyFiltersForm(criterias);
    }

    // Rebascule l'UI sur l'onglet « Catalogue » via le contrôleur
    // `listing-browser` (#305).
    switchToCatalog() {
        const controller = this.controllerFor('[data-controller~="listing-browser"]', "listing-browser");
        if (controller && typeof controller.showListForProfileLoad === "function") {
            controller.showListForProfileLoad();
        }
    }

    // Propage les favoris du profil actif au contrôleur `favorites` (#305).
    applyFavorites(data) {
        const favorites = data && data.favorites;
        if (!favorites) return;

        const controller = this.controllerFor('[data-controller~="favorites"]', "favorites");
        if (controller && typeof controller.applyProfile === "function") {
            controller.applyProfile({
                profileId: favorites.profile_id,
                ids: favorites.ids,
                addUrl: favorites.add_url,
                deleteUrl: favorites.delete_url,
                count: favorites.count,
                label: data.label,
            });
        }
    }

    // Récupère l'instance de contrôleur Stimulus associée à un élément, pour
    // appeler directement ses méthodes (les éléments cibles sont des frères du
    // panneau profil : un évènement qui « bubble » ne les atteindrait pas).
    controllerFor(selector, identifier) {
        try {
            const element = document.querySelector(selector);
            if (!element || !this.application) return null;
            return this.application.getControllerForElementAndIdentifier(element, identifier);
        } catch (_) {
            return null;
        }
    }

    refreshSimulation() {
        try {
            this.element.dispatchEvent(
                new CustomEvent("api-sync:refresh", {
                    bubbles: true,
                    detail: { scope: "listing_details", force: true },
                })
            );
        } catch (_) {}
    }

    // Relance la recherche catalogue (scope `listings_search`) après mise à jour
    // des filtres (#305). Réservé au cas où le catalogue est accessible (le
    // formulaire de filtres n'existe que dans ce cas).
    refreshListings() {
        if (!document.getElementById("listing-filter-form")) return;
        try {
            this.element.dispatchEvent(
                new CustomEvent("api-sync:refresh", {
                    bubbles: true,
                    detail: { scope: "listings_search", force: true },
                })
            );
        } catch (_) {}
    }

    // ---------- Utilitaires ----------
    buildUrl(template, id) {
        return String(template).replace("__ID__", encodeURIComponent(id));
    }

    csrfToken() {
        const meta = document.querySelector('meta[name="csrf-token"]');
        return meta ? meta.getAttribute("content") : "";
    }

    setButtonsDisabled(disabled) {
        if (!this.hasListTarget) return;
        this.listTarget
            .querySelectorAll(".simulator-profiles-card-kebab, .simulator-profiles-card-dropdown-item")
            .forEach((button) => {
                button.disabled = disabled;
            });
    }

    updateCount(count) {
        if (this.hasCountTarget) this.countTarget.textContent = String(count);
    }

    setStatus(message) {
        if (!this.hasStatusTarget) return;
        this.statusTarget.textContent = message;
        this.statusTarget.classList.remove("d-none");
    }

    clearStatus() {
        if (!this.hasStatusTarget) return;
        this.statusTarget.textContent = "";
        this.statusTarget.classList.add("d-none");
    }

    cssEscape(value) {
        if (window.CSS && typeof window.CSS.escape === "function") {
            return window.CSS.escape(value);
        }
        return String(value).replace(/["\\\]]/g, "\\$&");
    }
}
